import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import {
  applyThemeClass,
  parseThemePreference,
  readStoredTheme,
  resolveDarkMode,
  writeStoredTheme,
} from '../utils/theme';

const ThemeContext = createContext(null);

/**
 * Provides light / dark / system theme with localStorage persistence.
 */
export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => readStoredTheme());
  const [resolvedTheme, setResolvedTheme] = useState(() =>
    resolveDarkMode(readStoredTheme()) ? 'dark' : 'light',
  );

  const syncResolved = useCallback((preference) => {
    applyThemeClass(preference);
    setResolvedTheme(resolveDarkMode(preference) ? 'dark' : 'light');
  }, []);

  useEffect(() => {
    syncResolved(theme);
  }, [theme, syncResolved]);

  useEffect(() => {
    if (theme !== 'system') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => syncResolved('system');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme, syncResolved]);

  const setTheme = useCallback((next) => {
    const preference = parseThemePreference(next);
    writeStoredTheme(preference);
    setThemeState(preference);
  }, []);

  const value = useMemo(
    () => ({ theme, resolvedTheme, setTheme }),
    [theme, resolvedTheme, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** @returns {{ theme: import('../utils/theme').ThemePreference, resolvedTheme: 'light'|'dark', setTheme: (t: string) => void }} */
export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return ctx;
}
